import { useEffect, useRef, useState } from "react";
import { useDialogBehavior } from "@/hooks/useDialogBehavior";

interface PromptDialogProps {
  open: boolean;
  title: string;
  label: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

interface PromptFormProps {
  label: string;
  placeholder: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

function PromptFormActions({
  cancelLabel,
  confirmLabel,
  disabled,
  onCancel,
}: Readonly<{
  onCancel: () => void;
  cancelLabel: string;
  confirmLabel: string;
  disabled: boolean;
}>) {
  return (
    <div className="dialog-actions">
      <button type="button" onClick={onCancel}>
        {cancelLabel}
      </button>
      <button type="submit" className="primary" disabled={disabled}>
        {confirmLabel}
      </button>
    </div>
  );
}

function PromptForm(p: Readonly<PromptFormProps>) {
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue("");
    setTimeout(() => p.inputRef.current?.focus(), 0);
  }, []);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) {
          p.onConfirm(value.trim());
        }
      }}
    >
      <label className="dialog-label">{p.label}</label>
      <input
        ref={p.inputRef}
        type="text"
        className="dialog-input"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        placeholder={p.placeholder}
      />
      <PromptFormActions
        onCancel={p.onCancel}
        cancelLabel={p.cancelLabel}
        confirmLabel={p.confirmLabel}
        disabled={!value.trim()}
      />
    </form>
  );
}

export function PromptDialog({
  cancelLabel = "Cancel",
  confirmLabel = "Create",
  label,
  onCancel,
  onConfirm,
  open,
  placeholder = "",
  title,
}: Readonly<PromptDialogProps>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogBehavior(open, dialogRef, onCancel);

  if (!open) {
    return null;
  }

  return (
    <div className="dialog-overlay" onClick={onCancel} role="presentation">
      <div
        ref={dialogRef}
        className="dialog-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-dialog-title"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h3 id="prompt-dialog-title" className="dialog-title">
          {title}
        </h3>
        <PromptForm
          label={label}
          placeholder={placeholder}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          onConfirm={onConfirm}
          onCancel={onCancel}
          inputRef={inputRef}
        />
      </div>
    </div>
  );
}
